import type { DownloadAppInfoDTO, DownloadKeywordQuotaDTO } from '../types/tasks.types';

function compactUnique(values: string[]): string[] {
  const seen = new Set<string>();
  const items: string[] = [];

  values.forEach((value) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    const key = trimmed.toLocaleLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    items.push(trimmed);
  });

  return items;
}

function splitAppName(name: string): string[] {
  return name
    .split(/\s*[-–—－|｜:：]\s*/g)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function parseSearchKeywords(raw: string): string[] {
  return compactUnique(raw.split(/[\n,，]+/));
}

export function normalizeSearchKeywordQuotas(items: DownloadKeywordQuotaDTO[]): DownloadKeywordQuotaDTO[] {
  const seen = new Set<string>();
  const normalized: DownloadKeywordQuotaDTO[] = [];

  items.forEach((item) => {
    const keyword = item.keyword.trim();
    if (!keyword) return;
    const key = keyword.toLocaleLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const targetCount = Math.max(1, Math.floor(item.target_count || 0));
    const completedCount = Math.min(
      Math.max(0, Math.floor(item.completed_count || 0)),
      targetCount,
    );
    normalized.push({
      keyword,
      target_count: targetCount,
      completed_count: completedCount,
    });
  });

  return normalized;
}

export function sumSearchKeywordTargetCounts(items: DownloadKeywordQuotaDTO[]): number {
  return items.reduce((total, item) => total + Math.max(0, Math.floor(item.target_count || 0)), 0);
}

export function buildSearchKeywordQuotasFromKeywords(
  keywords: string[],
): DownloadKeywordQuotaDTO[] {
  return compactUnique(keywords).map((keyword) => ({
    keyword,
    target_count: 1,
    completed_count: 0,
  }));
}

export function buildLookupSearchKeywords(app: DownloadAppInfoDTO): string[] {
  const name = app.name.trim();
  if (!name) return [];

  return compactUnique([
    name,
    ...splitAppName(name),
  ]).slice(0, 5);
}
